<script>
import { useLayouts } from '@layouts'
import { NavFavoritesRail, VerticalNav } from '@layouts/components'
import { useNavGridLayout } from '@/composables/useNavGridLayout'
import { useNavFavoritesRail } from '@/composables/useNavFavoritesRail'
// DEVAMS-2670 second-round review: rail is now always vertical when shown
// (phone dropped it entirely) — 'orientation' concept retired.

export default defineComponent({
  props: {
    navItems: {
      type: Array,
      required: true,
    },
    pinnedNavItems: {
      type: Array,
      default: () => [],
    },
    verticalNavAttrs: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { slots }) {
    const { y: windowScrollY } = useWindowScroll()
    const { width: windowWidth } = useWindowSize()
    const { _layoutClasses: layoutClasses, isNavbarBlurEnabled } = useLayouts()
    // DEVAMS-2670: every tier now gets the same treatment — a small
    // always-visible favorites rail (outside the overlay drawer's hide/show
    // transform) plus a full 19-item nav that opens as a dimmed-scrim
    // overlay on every breakpoint (previously the wide tier rendered the
    // full grid persistently, which is what broke content alignment on
    // browser resize — a persistent full nav no longer exists at all).
    const navGridLayout = useNavGridLayout(windowWidth)
    const railItems = useNavFavoritesRail(toRef(props, 'pinnedNavItems'))
    const isOverlayNavActive = ref(false)
    const isLayoutOverlayVisible = ref(false)
    const toggleIsOverlayNavActive = useToggle(isOverlayNavActive)


    // ℹ️ This is alternative to below two commented watcher
    // We want to show overlay if overlay nav is visible and want to hide overlay if overlay is hidden and vice versa.
    syncRef(isOverlayNavActive, isLayoutOverlayVisible)

    // watch(isOverlayNavActive, value => {
    //   // Sync layout overlay with overlay nav
    //   isLayoutOverlayVisible.value = value
    // })
    // watch(isLayoutOverlayVisible, value => {
    //   // If overlay is closed via click, close hide overlay nav
    //   if (!value) isOverlayNavActive.value = false
    // })
    // DEVAMS-2670: the full nav is an overlay at every breakpoint now (no more
    // "persistent nav" mode to switch to on resize), so the previous
    // close-on-resize-wider watcher no longer applies.

    const router = useRouter()
    const shallShowPageLoading = ref(false)

    return () => {
      const verticalNavAttrs = toRef(props, 'verticalNavAttrs')
      const { wrapper: verticalNavWrapper, wrapperProps: verticalNavWrapperProps, ...additionalVerticalNavAttrs } = verticalNavAttrs.value


      // 👉 Vertical nav — DEVAMS-2670: ALWAYS an overlay now (dimmed scrim +
      // floating drawer), on every breakpoint, matching the iPad-portrait
      // interaction Jeroen approved. There is no more persistent/full-width
      // nav mode.
      const verticalNav = h(VerticalNav, { isOverlayNavActive: isOverlayNavActive.value, toggleIsOverlayNavActive, navItems: props.navItems, pinnedNavItems: props.pinnedNavItems, ...additionalVerticalNavAttrs }, {
        'nav-header': () => slots['vertical-nav-header']?.(),
        'before-nav-items': () => slots['before-vertical-nav-items']?.(),
      })

      // 👉 DEVAMS-2670 second-round review: the always-visible favorites rail
      // now ONLY renders on tablet/web (navGridLayout.showRail) — phone
      // dropped it entirely in favor of the pre-existing bottom tab bar
      // (Footer.vue), which is wired to the same pinned-items data via
      // useNavBottomBarItems. Lives outside the overlay drawer's hide/show
      // transform so it's never hidden.
      const showRail = navGridLayout.value.showRail
      const favoritesRail = showRail
        ? h(NavFavoritesRail, {
          items: railItems.value,
          size: navGridLayout.value.railSize,
          variant: navGridLayout.value.railVariant,
          showToggle: navGridLayout.value.railShowToggle,
          onOpenNav: () => toggleIsOverlayNavActive(true),
        })
        : null


      // 👉 Navbar
      const navbar = h('header', { class: ['layout-navbar', { 'navbar-blur': isNavbarBlurEnabled.value }] }, [
        h('div', { class: 'navbar-content-container' }, slots.navbar?.({
          toggleVerticalOverlayNavActive: toggleIsOverlayNavActive,
          // DEVAMS-2670 fourth-round review: the global page header must only
          // show its own ☰ toggle on phone (the only tier with no rail at
          // all — showRail is false there). Every tier that renders the
          // favorites rail already owns the SOLE ☰/"More…" control in the
          // rail's own header/list (see NavFavoritesRail.vue), so the page
          // header must not duplicate it.
          showRail,
        })),
      ])


      // 👉 Content area
      let mainChildren = slots.default?.()

      // 💡 Only show loading and attach `beforeEach` & `afterEach` hooks if `content-loading` slot is used
      if (slots['content-loading']) {
        router.beforeEach(() => {
          console.info('setting to true')
          shallShowPageLoading.value = true
        })
        router.afterEach(() => {
          console.info('setting to false')
          shallShowPageLoading.value = false
        })
        mainChildren = shallShowPageLoading.value ? slots['content-loading']?.() : slots.default?.()
      }
      const main = h('main', { class: 'layout-page-content' }, h('div', { class: 'page-content-container' }, mainChildren))


      // 👉 Footer
      const footer = h('footer', { class: 'layout-footer' }, [
        h('div', { class: 'footer-content-container' }, slots.footer?.()),
      ])


      // 👉 Overlay (dims page content while the full nav drawer is open)
      const layoutOverlay = h('div', {
        class: ['layout-overlay', { visible: isLayoutOverlayVisible.value }],
        onClick: () => { isLayoutOverlayVisible.value = !isLayoutOverlayVisible.value },
      })

      // DEVAMS-2670: content is pushed over by the fixed-width favorites rail
      // (never by the full nav, which floats above as an overlay) whenever
      // the rail is shown. Reserving space for a rail whose width never
      // changes within a tier is what fixes the DEVAMS-2670 review bug where
      // resizing the window misaligned nav/content.
      const contentWrapperStyle = showRail
        ? { paddingInlineStart: `${navGridLayout.value.railSize}px` }
        : undefined

      return h('div', { class: ['layout-wrapper', ...layoutClasses.value(windowWidth.value, windowScrollY.value)] }, [
        verticalNavWrapper ? h(verticalNavWrapper, verticalNavWrapperProps, { default: () => verticalNav }) : verticalNav,
        favoritesRail,
        h('div', { class: 'layout-content-wrapper', style: contentWrapperStyle }, [
          navbar,
          main,
          footer,
        ]),
        layoutOverlay,
      ])
    }
  },
})
</script>

<style lang="scss">
@use "@configured-variables" as variables;
@use "@layouts/styles/placeholders";
@use "@layouts/styles/mixins";

.layout-wrapper.layout-nav-type-vertical {
  // TODO(v2): Check why we need height in vertical nav & min-height in horizontal nav
  block-size: 100%;

  .layout-content-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-block-size: calc(var(--vh, 1vh) * 100);
    transition: padding-inline-start 0.2s ease-in-out;
    will-change: padding-inline-start;
    background-color: rgb(var(--v-theme-background));
  }

  .layout-navbar {
    z-index: variables.$layout-vertical-nav-layout-navbar-z-index;

    .navbar-content-container {
      block-size: variables.$layout-vertical-nav-navbar-height;
    }

    @at-root {
      .layout-wrapper.layout-nav-type-vertical {
        .layout-navbar {
          @if variables.$layout-vertical-nav-navbar-is-contained {
            @include mixins.boxed-content;
          } @else {
            .navbar-content-container {
              @include mixins.boxed-content;
            }
          }
        }
      }
    }
  }

  &.layout-navbar-sticky .layout-navbar {
    @extend %layout-navbar-sticky;
  }

  &.layout-navbar-hidden .layout-navbar {
    @extend %layout-navbar-hidden;
  }

  // 👉 Footer
  .layout-footer {
    @include mixins.boxed-content;
  }

  // 👉 Layout overlay
  .layout-overlay {
    position: fixed;
    z-index: variables.$layout-overlay-z-index;
    background-color: rgb(0 0 0 / 60%);
    cursor: pointer;
    inset: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease-in-out;
    will-change: transform;

    &.visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &:not(.layout-overlay-nav) .layout-content-wrapper {
    padding-inline-start: variables.$layout-vertical-nav-width;
  }

  // Adjust right column pl when vertical nav is collapsed
  &.layout-vertical-nav-collapsed .layout-content-wrapper {
    padding-inline-start: variables.$layout-vertical-nav-collapsed-width;
  }

  // 👉 Content height fixed
  &.layout-content-height-fixed {
    .layout-content-wrapper {
      max-block-size: calc(var(--vh) * 100);
    }

    .layout-page-content {
      display: flex;
      overflow: hidden;

      .page-content-container {
        inline-size: 100%;

        > :first-child {
          max-block-size: 100%;
          overflow-y: auto;
        }
      }
    }
  }
}
</style>
